import { Link } from 'react-router-dom';
import { Home, Building2, Gamepad2, Trophy, Users, Target, Star, ChevronRight, Sparkles, Heart, Keyboard } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BlockyCharacter3D from '../components/BlockyCharacter3D';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-orange-50">
      <Navbar />
      
      <main id="main-content" role="main">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden" aria-labelledby="hero-headline">
          {/* Friendly decorative elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-sunny-yellow/20 rounded-full blur-[60px]" aria-hidden="true" />
          <div className="absolute top-40 right-20 w-40 h-40 bg-mint-green/20 rounded-full blur-[80px]" aria-hidden="true" />
          <div className="absolute bottom-20 left-1/4 w-48 h-48 bg-lavender/15 rounded-full blur-[100px]" aria-hidden="true" />
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center">
              {/* Friendly badge */}
              <div className="inline-flex items-center gap-2 bg-white shadow-lg rounded-full px-5 py-2.5 mb-8 border-2 border-mint-green/30">
                <Sparkles className="w-5 h-5 text-sunny-yellow" aria-hidden="true" />
                <span className="font-bold text-mint-green">Fun typing for ages 5-11!</span>
              </div>
              
              {/* Main Headline */}
              <h1 className="font-display text-6xl md:text-8xl font-bold mb-6" id="hero-headline" data-testid="hero-headline">
                <span className="text-mint-green">Type</span>
                <span className="text-coral-orange">Town</span>
              </h1>
              
              <p className="font-display text-2xl md:text-3xl text-text-dark font-bold mb-6">
                Type. Play. Grow! 
                <Heart className="w-7 h-7 inline ml-2 text-coral-orange fill-coral-orange" aria-hidden="true" />
              </p>
              
              <p className="text-lg md:text-xl text-text-medium max-w-2xl mx-auto mb-10 leading-relaxed">
                Build your own town! Meet friendly neighbours. Learn typing through adventure - 
                every keystroke makes your world grow bigger and happier!
              </p>
              
              {/* Character Preview */}
              <div className="flex justify-center mb-10" aria-label="Example characters">
                <div className="flex gap-4 items-end">
                  <BlockyCharacter3D size={80} skinTone="#3D2314" hairColor="#FF69B4" shirtColor="#EC4899" pantsColor="#3B82F6" hairStyle="braids" accessory="glasses" eyeStyle="happy" />
                  <BlockyCharacter3D size={100} skinTone="#8D5524" hairColor="#00CED1" shirtColor="#22C55E" pantsColor="#1F2937" hairStyle="afro" accessory="headband" eyeStyle="happy" />
                  <BlockyCharacter3D size={80} skinTone="#F5D0B0" hairColor="#8B5CF6" shirtColor="#F59E0B" pantsColor="#7C3AED" hairStyle="ponytail" accessory="bow" />
                </div>
              </div>
              
              {/* Mode Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
                <Link 
                  to="/for-schools"
                  className="group bg-white rounded-3xl p-6 shadow-lg border-2 border-gray-100 hover:border-mint-green hover:scale-105 transition-all"
                  data-testid="hero-school-card"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-6 h-6 text-mint-green" aria-hidden="true" />
                    <span className="font-bold text-mint-green">For Schools</span>
                  </div>
                  <h2 className="font-display text-2xl font-bold text-text-dark mb-2">School Mode</h2>
                  <p className="text-text-medium mb-4">
                    Class challenges, friendly competitions, and teacher dashboard included!
                  </p>
                  <span className="inline-flex items-center gap-1 text-mint-green font-bold group-hover:gap-2 transition-all">
                    Learn more <ChevronRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </Link>
                
                <Link 
                  to="/for-parents"
                  className="group bg-white rounded-3xl p-6 shadow-lg border-2 border-gray-100 hover:border-sunny-yellow hover:scale-105 transition-all"
                  data-testid="hero-solo-card"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Gamepad2 className="w-6 h-6 text-sunny-yellow" aria-hidden="true" />
                    <span className="font-bold text-sunny-yellow">Home Play</span>
                  </div>
                  <h2 className="font-display text-2xl font-bold text-text-dark mb-2">Solo Mode</h2>
                  <p className="text-text-medium mb-4">
                    Play anywhere, anytime. Practice at five fun levels. Climb the leaderboard!
                  </p>
                  <span className="inline-flex items-center gap-1 text-sunny-yellow font-bold group-hover:gap-2 transition-all">
                    Learn more <ChevronRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </Link>
              </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login?mode=school&signup=true"
                data-testid="hero-school-signup-btn"
                className="font-bold bg-gradient-to-r from-mint-green to-sky-blue text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg"
              >
                Start Free - Schools
              </Link>
              <Link
                to="/login?mode=solo&signup=true"
                data-testid="hero-solo-signup-btn"
                className="font-bold bg-gradient-to-r from-sunny-yellow to-coral-orange text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg"
              >
                Play Solo Free!
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-20 px-4 bg-white" aria-labelledby="how-it-works-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-bold text-lavender">How It Works</span>
            <h2 id="how-it-works-heading" className="font-display text-4xl md:text-5xl font-bold text-text-dark mt-2">Three Easy Steps!</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-b from-mint-green/10 to-white rounded-3xl p-8 text-center shadow-lg border-2 border-mint-green/20" data-testid="step-1">
              <div className="w-20 h-20 bg-mint-green rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Target className="w-10 h-10 text-white" aria-hidden="true" />
              </div>
              <div className="text-5xl font-display font-bold text-mint-green/30 mb-2" aria-hidden="true">1</div>
              <h3 className="font-display text-2xl font-bold text-mint-green mb-4">Type Accurately</h3>
              <p className="text-text-medium">Hit the right keys and watch your town grow bigger!</p>
            </div>
            
            <div className="bg-gradient-to-b from-sunny-yellow/10 to-white rounded-3xl p-8 text-center shadow-lg border-2 border-sunny-yellow/20" data-testid="step-2">
              <div className="w-20 h-20 bg-sunny-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Users className="w-10 h-10 text-white" aria-hidden="true" />
              </div>
              <div className="text-5xl font-display font-bold text-sunny-yellow/30 mb-2" aria-hidden="true">2</div>
              <h3 className="font-display text-2xl font-bold text-sunny-yellow mb-4">Meet Neighbours</h3>
              <p className="text-text-medium">Chat with friendly townspeople and complete typing quests!</p>
            </div>
            
            <div className="bg-gradient-to-b from-lavender/10 to-white rounded-3xl p-8 text-center shadow-lg border-2 border-lavender/20" data-testid="step-3">
              <div className="w-20 h-20 bg-lavender rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Star className="w-10 h-10 text-white" aria-hidden="true" />
              </div>
              <div className="text-5xl font-display font-bold text-lavender/30 mb-2" aria-hidden="true">3</div>
              <h3 className="font-display text-2xl font-bold text-lavender mb-4">Earn & Grow</h3>
              <p className="text-text-medium">Collect coins, customise your look, and level up!</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Competition Modes */}
      <section className="py-20 px-4 bg-gradient-to-b from-sky-50 to-white" aria-labelledby="challenges-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-bold text-coral-orange">Fun Challenges</span>
            <h2 id="challenges-heading" className="font-display text-4xl md:text-5xl font-bold text-text-dark mt-2">Four Ways to Play!</h2>
            <p className="text-text-medium mt-4 max-w-2xl mx-auto">
              All challenges focus on accuracy, not just speed - so everyone can succeed!
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Keyboard, title: 'Friendly Duel', scope: '1 vs 1', desc: 'Challenge a friend to a typing match!', color: 'coral-orange', freq: 'Anytime' },
              { icon: Building2, title: 'Class Challenge', scope: 'Class vs Class', desc: 'Compete with other classes. Go team!', color: 'mint-green', freq: 'Weekly' },
              { icon: Users, title: 'Year Rally', scope: 'Year vs Year', desc: 'Your whole year works together!', color: 'lavender', freq: 'Monthly' },
              { icon: Trophy, title: 'Town Festival', scope: 'Whole School', desc: 'Everyone builds the biggest town ever!', color: 'sunny-yellow', freq: 'Special' },
            ].map((comp, i) => (
              <div key={i} className={`bg-white rounded-3xl p-6 shadow-lg border-2 border-${comp.color}/20 hover:border-${comp.color} hover:scale-105 transition-all`} data-testid={`comp-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-bold text-text-medium">{comp.scope}</span>
                  <div className={`w-10 h-10 bg-${comp.color}/20 rounded-full flex items-center justify-center`}>
                    <comp.icon className={`w-5 h-5 text-${comp.color}`} aria-hidden="true" />
                  </div>
                </div>
                <h3 className="font-display text-xl font-bold text-text-dark mb-2">{comp.title}</h3>
                <p className="text-sm text-text-medium mb-4">{comp.desc}</p>
                <span className={`text-xs font-bold text-${comp.color}`}>{comp.freq}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Levels Preview */}
      <section className="py-20 px-4 bg-white" aria-labelledby="levels-heading">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-bold text-mint-green">Your Journey</span>
              <h2 id="levels-heading" className="font-display text-4xl md:text-5xl font-bold text-text-dark mt-2 mb-6">Five Fun Levels!</h2>
              <p className="text-lg text-text-medium mb-6">
                Start as a Newcomer and work your way up to Champion! 
                Each level unlocks new customizations and bigger towns.
              </p>
              <div className="space-y-3">
                {[
                  { stage: 'Newcomer', year: 'Y1-Y2', desc: 'Just starting your adventure!', color: '#FF6B6B' },
                  { stage: 'Explorer', year: 'Y2-Y3', desc: 'Discovering new places', color: '#FFD93D' },
                  { stage: 'Builder', year: 'Y3-Y4', desc: 'Building amazing things', color: '#6BCB77' },
                  { stage: 'Master', year: 'Y4-Y5', desc: 'Teaching others to type', color: '#4ECDC4' },
                  { stage: 'Champion', year: 'Y5-Y6', desc: 'The ultimate typer!', color: '#A66CFF' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-4 bg-gray-50 rounded-2xl p-4 border-2 border-gray-100">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-white shadow-md"
                      style={{ backgroundColor: s.color }}
                      aria-hidden="true"
                    >
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-text-dark">{s.stage}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-text-medium">{s.year}</span>
                      </div>
                      <p className="text-sm text-text-medium">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-center" aria-hidden="true">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-b from-mint-green/20 to-sunny-yellow/20 rounded-full blur-[80px]" />
                <div className="relative bg-gradient-to-b from-sky-100 to-mint-green/20 rounded-3xl p-10">
                  <BlockyCharacter3D 
                    size={200} 
                    skinTone="#5C3A21" 
                    hairColor="#00CED1" 
                    shirtColor="#EC4899" 
                    pantsColor="#7C3AED"
                    hairStyle="braids"
                    eyeStyle="happy"
                    accessory="crown"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-mint-green via-sky-blue to-lavender" aria-labelledby="cta-heading">
        <div className="max-w-4xl mx-auto text-center">
          <h2 id="cta-heading" className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Adventure?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Join thousands of happy typers building their towns today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/trial"
              className="font-bold bg-white text-mint-green px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg"
            >
              Schools - Get Started Free
            </Link>
            <Link
              to="/login"
              className="font-bold bg-sunny-yellow text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg"
            >
              Start Playing Now!
            </Link>
          </div>
        </div>
      </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default HomePage;
